import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DocumentTextIcon, FolderIcon } from '@heroicons/react/24/outline'
import Button from '../ui/Button'
import type { ExistingWeldSummary, UploadCompletePayload } from './UploadWeldDataDialog'
import ReuploadWarningBanner from './weldUpload/ReuploadWarningBanner'
import AlxWeldIdMismatchDialog from './weldUpload/AlxWeldIdMismatchDialog'
import AlxMultiWeldIdDialog from './weldUpload/AlxMultiWeldIdDialog'
import AlxModalShell from './weldUpload/AlxModalShell'
import { detectAlxAnomalies } from '../../utils/alx/detectAlxAnomalies'
import { mapAlxToAggregate, identityAlxPassMappings } from '../../utils/alx/mapToAggregate'
import { processAlxFiles, readAlxFileContents } from '../../utils/alx/processAlxFiles'
import { detectAlxWeldIds } from '../../utils/alx/weldIdFromPath'
import { fileSelectionListKey } from '../../utils/weldUpload/mergeFileSelections'
import type { AlxPassRow, CwCcwMode } from '../../utils/alx/types'
import {
  buildAlxWeldParamsSection,
  finalizeAlxUploadForFiles,
  type BatchWeldDraft
} from '../../utils/alx/alxUploadPipeline'
import type { AlxSingleRenameDraft } from './weldUpload/AlxMultiWeldIdDialog'
import { buildWeldParamsUploadSection } from '../../utils/weldUpload/buildWeldParamsSection'
import { validateAggregate } from '../../utils/weldUpload/validateAggregate'
import { uploadAlxSummary } from '../../utils/alxSummaryClient'
import type { EmbeddedFooterActions } from './weldUpload/embeddedFooterActions'

interface ManualAlxUploadDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
  pipeSetId: string
  weldId: string
  floorSheetWeldLabel?: string
  wpsPassLabels: string[]
  onUploadComplete: (payload: UploadCompletePayload) => void
  existingSummary?: ExistingWeldSummary | null
  onApplyWeldId?: (rowId: string, weldIdLabel: string) => void
  onCreateWeldWithId?: (weldIdLabel: string) => string
  onPrepareBatch?: (
    groups: Map<string, File[]>,
    cwCcwMode: CwCcwMode
  ) => Promise<BatchWeldDraft[]>
  onFinalizeBatch?: (drafts: BatchWeldDraft[]) => Promise<void>
  embedded?: boolean
  initialFiles?: File[]
  hideFilePicker?: boolean
  onFilesChange?: (files: File[]) => void
  allowCreateMultiple?: boolean
  onFooterActionsChange?: (actions: EmbeddedFooterActions | null) => void
}

const CW_CCW_MODE: CwCcwMode = 'separate'

export default function ManualAlxUploadDialog({
  open,
  onClose,
  projectId,
  pipeSetId,
  weldId,
  floorSheetWeldLabel = '',
  wpsPassLabels,
  onUploadComplete,
  existingSummary,
  onApplyWeldId,
  onCreateWeldWithId,
  onPrepareBatch,
  onFinalizeBatch,
  embedded = false,
  initialFiles,
  hideFilePicker = false,
  onFilesChange,
  allowCreateMultiple,
  onFooterActionsChange
}: ManualAlxUploadDialogProps) {
  const folderInputRef = useRef<HTMLInputElement>(null)
  const lastInitialFilesKeyRef = useRef('')
  const [files, setFiles] = useState<File[]>([])
  const [rows, setRows] = useState<AlxPassRow[]>([])
  const [parseErrors, setParseErrors] = useState<Array<{ fileName: string; message: string }>>([])
  const [busy, setBusy] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [confirmPending, setConfirmPending] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [detectedWeldId, setDetectedWeldId] = useState<string | null>(null)
  const [showWeldIdMismatch, setShowWeldIdMismatch] = useState(false)
  const [weldIdGateOpen, setWeldIdGateOpen] = useState(false)
  const [multiWeldIds, setMultiWeldIds] = useState<string[]>([])
  const [showMultiWeldDialog, setShowMultiWeldDialog] = useState(false)

  const rawPassNames = useMemo(
    () => Array.from(new Set(rows.map((r) => r.passName))).sort(),
    [rows]
  )
  const passMappings = useMemo(() => identityAlxPassMappings(rawPassNames), [rawPassNames])

  const anomalies = useMemo(() => detectAlxAnomalies(rows), [rows])

  useEffect(() => {
    if (!open) {
      setFiles([])
      setRows([])
      setParseErrors([])
      setBusy(false)
      setServerError(null)
      setConfirmPending(false)
      setDetectedWeldId(null)
      setShowWeldIdMismatch(false)
      setWeldIdGateOpen(false)
      setMultiWeldIds([])
      setShowMultiWeldDialog(false)
      lastInitialFilesKeyRef.current = ''
    }
  }, [open])

  useEffect(() => {
    if (!embedded || !initialFiles) return
    if (initialFiles.length === 0) {
      lastInitialFilesKeyRef.current = ''
      setFiles([])
      setRows([])
      return
    }
    const nextKey = fileSelectionListKey(initialFiles)
    if (nextKey === lastInitialFilesKeyRef.current) return
    lastInitialFilesKeyRef.current = nextKey
    void parseFolder(initialFiles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, initialFiles])

  const aggregatePreview = useMemo(
    () => mapAlxToAggregate(rows, passMappings),
    [rows, passMappings]
  )

  const wizardActive = showMultiWeldDialog && multiWeldIds.length > 1

  const canUpload =
    rows.length > 0 &&
    validateAggregate(aggregatePreview) &&
    !busy &&
    !weldIdGateOpen &&
    !wizardActive

  const hasExisting = Boolean(existingSummary?.source)

  function reconcileDetectedWeldId(txtFiles: File[], mode: CwCcwMode) {
    const { weldIds, ambiguous } = detectAlxWeldIds(txtFiles, mode)
    if (ambiguous) {
      setMultiWeldIds(weldIds)
      setShowMultiWeldDialog(true)
      setDetectedWeldId(null)
      setShowWeldIdMismatch(false)
      setWeldIdGateOpen(true)
      return
    }
    setMultiWeldIds([])
    setShowMultiWeldDialog(false)
    const detected = weldIds[0] ?? null
    setDetectedWeldId(detected)
    const floorLabel = floorSheetWeldLabel.trim()
    if (!detected) {
      setShowWeldIdMismatch(false)
      setWeldIdGateOpen(false)
      return
    }
    if (!floorLabel) {
      onApplyWeldId?.(weldId, detected)
      setShowWeldIdMismatch(false)
      setWeldIdGateOpen(false)
      return
    }
    if (floorLabel !== detected) {
      setShowWeldIdMismatch(true)
      setWeldIdGateOpen(true)
      return
    }
    setShowWeldIdMismatch(false)
    setWeldIdGateOpen(false)
  }

  async function parseFolder(selected: File[]) {
    setServerError(null)
    const txtFiles = selected.filter((f) => f.name.toLowerCase().endsWith('.txt'))
    setFiles(txtFiles)
    if (!embedded) {
      onFilesChange?.(txtFiles)
    }
    if (txtFiles.length === 0) {
      setRows([])
      setParseErrors([{ fileName: '', message: 'No .txt files found in the selected folder.' }])
      setWeldIdGateOpen(false)
      setShowWeldIdMismatch(false)
      setShowMultiWeldDialog(false)
      return
    }
    const contents = await readAlxFileContents(txtFiles)
    const result = processAlxFiles({ files: txtFiles, contents, cwCcwMode: CW_CCW_MODE })
    setRows(result.rows)
    setParseErrors(result.errors)
    reconcileDetectedWeldId(txtFiles, CW_CCW_MODE)
  }

  async function runUpload() {
    if (!canUpload) return
    setBusy(true)
    setServerError(null)
    try {
      const uploadedAt = new Date().toISOString()
      const sourceFilenames = files.map((f) => f.name)
      await uploadAlxSummary({
        projectId,
        pipeSetId,
        weldId,
        aggregate: aggregatePreview,
        passMappings,
        sourceFilenames,
        cwCcwMode: CW_CCW_MODE,
        uploadedAt,
        replaceExisting: true
      })
      const section = buildWeldParamsUploadSection({
        aggregate: aggregatePreview,
        passMappings,
        sourceFilenames,
        uploadedAt,
        uploadFormat: 'alx-summary',
        cwCcwMode: CW_CCW_MODE
      })
      onUploadComplete({
        aggregate: section.aggregate,
        sourceFilename: section.sourceFilename,
        sourceFilenames: section.sourceFilenames,
        passMappings: section.passMappings,
        uploadedAt: section.uploadedAt,
        filenameMeta: { pipeSize: null, station: null, direction: null },
        uploadFormat: 'alx-summary'
      })
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setBusy(false)
      setConfirmPending(false)
    }
  }

  async function handleFinalizeSingle(draft: AlxSingleRenameDraft) {
    setBusy(true)
    setServerError(null)
    try {
      const result = await finalizeAlxUploadForFiles({
        projectId,
        pipeSetId,
        weldRowId: weldId,
        files: draft.files,
        rows: draft.rows,
        passMappings: draft.passMappings,
        cwCcwMode: CW_CCW_MODE
      })
      const section = buildAlxWeldParamsSection(result, CW_CCW_MODE)
      setShowMultiWeldDialog(false)
      setWeldIdGateOpen(false)
      onUploadComplete({
        aggregate: section.aggregate,
        sourceFilename: section.sourceFilename,
        sourceFilenames: section.sourceFilenames,
        passMappings: section.passMappings,
        uploadedAt: section.uploadedAt,
        filenameMeta: { pipeSize: null, station: null, direction: null },
        uploadFormat: 'alx-summary'
      })
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Import failed')
      setBusy(false)
      throw err
    }
  }

  function handleUploadClick() {
    if (!canUpload) return
    if (hasExisting && !confirmPending) {
      setConfirmPending(true)
      return
    }
    void runUpload()
  }

  const handleUploadClickRef = useRef(handleUploadClick)
  handleUploadClickRef.current = handleUploadClick

  useEffect(() => {
    if (!embedded || !onFooterActionsChange || !open) return
    onFooterActionsChange({
      canUpload,
      busy,
      label: confirmPending ? 'Confirm upload' : 'Upload',
      onUpload: () => handleUploadClickRef.current()
    })
    return () => onFooterActionsChange(null)
  }, [embedded, onFooterActionsChange, open, canUpload, busy, confirmPending])

  if (!open) return null

  const uploadBody = wizardActive ? (
    <p className="text-sm text-heritage-grey" data-testid="manual-alx-wizard-active-hint">
      Complete the multi-weld wizard to continue, or cancel it to adjust folder settings.
    </p>
  ) : (
    <>
      {hasExisting && existingSummary ? (
        <ReuploadWarningBanner
          existingSummary={existingSummary}
          formatSwitchWarning={
            existingSummary.source === 'upload'
              ? 'Replacing with ALX summary data — Pass Analytics will not be available for this weld.'
              : null
          }
        />
      ) : null}

      {confirmPending ? (
        <p className="text-sm text-warning-800" data-testid="manual-alx-confirm-reupload">
          Click Upload again to confirm replacing existing data.
        </p>
      ) : null}

      <div>
        {!hideFilePicker ? (
        <label
          htmlFor="manual-alx-folder"
          data-testid="manual-alx-dropzone"
          data-dragover={dragOver ? 'true' : undefined}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            dragOver
              ? 'border-hero-red bg-hero-red-10/40'
              : 'border-gray-300 bg-white hover:border-hero-red hover:bg-hero-red-10/30'
          } ${busy ? 'pointer-events-none opacity-60' : ''}`}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            const dropped = Array.from(e.dataTransfer.files ?? [])
            if (dropped.length > 0) void parseFolder(dropped)
          }}
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-hero-red-10 text-hero-red">
            <FolderIcon className="h-6 w-6" aria-hidden />
          </span>
          <span className="text-sm font-semibold text-blueprint-blue">
            Click to choose a folder or drag files here
          </span>
          <span className="text-[11px] text-heritage-grey">
            All .txt files in the folder will be imported as weld passes
          </span>
          <input
            ref={folderInputRef}
            id="manual-alx-folder"
            type="file"
            accept=".txt,text/plain"
            multiple
            // @ts-expect-error webkitdirectory is non-standard but widely supported
            webkitdirectory=""
            className="sr-only"
            onChange={(e) => {
              const selected = Array.from(e.target.files ?? [])
              if (selected.length > 0) void parseFolder(selected)
            }}
            disabled={busy}
          />
        </label>
        ) : null}
      </div>

      {files.length > 0 ? (
        <p className="text-[11px] text-gray-500" data-testid="manual-alx-file-count">
          {files.length} file{files.length === 1 ? '' : 's'} · {rows.length} pass
          {rows.length === 1 ? '' : 'es'} parsed
        </p>
      ) : null}

      {parseErrors.length > 0 ? (
        <ul className="space-y-1 text-[11px] text-error-600" data-testid="manual-alx-parse-errors">
          {parseErrors.map((err, i) => (
            <li key={`${err.fileName}-${i}`}>
              {err.fileName ? `${err.fileName}: ` : ''}
              {err.message}
            </li>
          ))}
        </ul>
      ) : null}

      {anomalies.length > 0 ? (
        <ul className="space-y-1 rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-[11px] text-warning-800">
          {anomalies.map((w, i) => (
            <li key={`${w.fileName}-${i}`}>
              <strong>{w.passName}</strong> ({w.fileName}): {w.message}
            </li>
          ))}
        </ul>
      ) : null}

      {rows.length > 0 ? (
        <ul className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100 text-[11px]">
          {rows.map((row) => (
            <li key={`${row.fileName}-${row.passName}`} className="flex items-center gap-2 px-3 py-2">
              <DocumentTextIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
              <span className="truncate font-medium text-gray-800">{row.fileName}</span>
              <span className="text-gray-400">→</span>
              <span className="truncate text-gray-600">{row.passName}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {/* Manual ALX uses pass names from the logs directly (no Rename Pass). */}
    </>
  )

  const uploadFooter = (
    <div className="flex items-center justify-end gap-2">
      {!embedded ? (
        <Button variant="secondary" type="button" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      ) : null}
      <Button
        type="button"
        data-testid="manual-alx-upload-submit"
        disabled={!canUpload}
        onClick={handleUploadClick}
      >
        {busy ? 'Uploading…' : confirmPending ? 'Confirm upload' : 'Upload'}
      </Button>
    </div>
  )

  const dialogExtras = (
    <>
      <AlxWeldIdMismatchDialog
        open={showWeldIdMismatch && Boolean(detectedWeldId)}
        floorSheetWeldId={floorSheetWeldLabel}
        detectedWeldId={detectedWeldId ?? ''}
        onClose={() => {
          setShowWeldIdMismatch(false)
          setWeldIdGateOpen(true)
        }}
        onChangeWeldId={() => {
          if (detectedWeldId) onApplyWeldId?.(weldId, detectedWeldId)
          setShowWeldIdMismatch(false)
          setWeldIdGateOpen(false)
        }}
        onCreateNewWeld={() => {
          if (!detectedWeldId || !onCreateWeldWithId) return
          onCreateWeldWithId(detectedWeldId)
          setShowWeldIdMismatch(false)
          setWeldIdGateOpen(false)
        }}
      />
      <AlxMultiWeldIdDialog
        open={wizardActive}
        weldIds={multiWeldIds}
        files={files}
        cwCcwMode={CW_CCW_MODE}
        wpsPassLabels={wpsPassLabels}
        busy={busy}
        onClose={() => {
          setShowMultiWeldDialog(false)
          setWeldIdGateOpen(true)
        }}
        allowCreateMultiple={
          allowCreateMultiple ?? Boolean(onPrepareBatch && onFinalizeBatch)
        }
        onPrepareBatch={onPrepareBatch ?? (async () => [])}
        onFinalizeBatch={onFinalizeBatch ?? (async () => {})}
        onApplyWeldIdForPickOne={(label) => onApplyWeldId?.(weldId, label)}
        onFinalizeSingle={handleFinalizeSingle}
      />
    </>
  )

  if (embedded) {
    return (
      <>
        <div className={`space-y-4 ${wizardActive ? 'opacity-40 pointer-events-none' : ''}`}>
          {uploadBody}
          {serverError ? (
            <p role="alert" className="text-sm text-error-600">
              {serverError}
            </p>
          ) : null}
        </div>
        {dialogExtras}
      </>
    )
  }

  return (
    <>
      {createPortal(
        <AlxModalShell
          open
          onClose={onClose}
          title="Manual Weld Upload"
          subtitle="Upload a folder of ALX summary log files (.txt) for this weld."
          icon={<FolderIcon className="h-5 w-5" />}
          width="xl"
          zIndex={10000}
          testId="manual-alx-upload-dialog"
          titleId="manual-alx-upload-title"
          closeDisabled={busy}
          footer={uploadFooter}
        >
          <div className={`space-y-4 ${wizardActive ? 'opacity-40 pointer-events-none' : ''}`}>
            {uploadBody}
            {serverError ? (
              <p role="alert" className="text-sm text-error-600">
                {serverError}
              </p>
            ) : null}
          </div>
        </AlxModalShell>,
        document.body
      )}
      {dialogExtras}
    </>
  )
}
